import React, { useRef, useEffect, useCallback } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import SkuflixChevronRightIcon from "core/icons/skuflix/SkuflixChevronRightIcon";
import useCarousel from "core/hooks/useCarousel";
import { alpha } from "@mui/system";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LoadMore from "core/components/LoadMore";
import Item from "./Item";

const PREFIX = "ArenaPostsCarousel";

const classes = {
  scrollable: `${PREFIX}-scrollable`,
  grid: `${PREFIX}-grid`,
  item: `${PREFIX}-item`,
  prev: `${PREFIX}-prev`,
  next: `${PREFIX}-next`,
  btnRoot: `${PREFIX}-btnRoot`,
  icon: `${PREFIX}-icon`,
};

const Root = styled("div")(({ theme }) => ({
  position: "relative",
  overflow: "hidden",

  [`& .${classes.scrollable}`]: {
    overflowY: "hidden",
    overflowX: "auto",
    msOverflowStyle: "none", // IE 10+
    scrollbarWidth: "none", // Firefox
    "&::-webkit-scrollbar": {
      display: "none", // Safari and Chrome
    },
    scrollSnapType: "x mandatory",
  },

  [`& .${classes.grid}`]: {
    flexWrap: "nowrap",
  },

  [`& .${classes.item}`]: {
    flexShrink: 0,
    scrollSnapAlign: "start",
  },

  [`& .${classes.prev}`]: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    zIndex: 1,
    width: 0,
    height: "100%",
    pointerEvents: "none",
    "& button": {
      left: 0,
      backgroundImage: `linear-gradient(90deg, ${
        theme.palette.background.body
      }, ${alpha(theme.palette.background.body, 0.6)}, transparent)`,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.next}`]: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 1,
    width: 0,
    height: "100%",
    pointerEvents: "none",
    "& button": {
      right: 0,
      backgroundImage: `linear-gradient(-90deg, ${
        theme.palette.background.body
      }, ${alpha(theme.palette.background.body, 0.6)}, transparent)`,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.btnRoot}`]: {
    pointerEvents: "all",
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 40,
    backgroundColor: "transparent",
    color: "rgba(255, 255, 255, 0.3)",
    "&:hover": {
      color: "#fff",
    },
    [theme.breakpoints.up("sm")]: {
      width: 60,
    },
    [theme.breakpoints.up("md")]: {
      width: 70,
    },
  },

  [`& .${classes.icon}`]: {
    fontSize: 40,
    [theme.breakpoints.up("sm")]: {
      fontSize: 50,
    },
  },
}));

export default function Carousel({
  items,
  sm,
  md,
  lg,
  xl,
  variant,
  hasMore,
  onLoadMore,
  loading,
  nextPage,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));

  const { rootRef, onScroll, prev, next, onClickPrev, onClickNext } =
    useCarousel(items.length);

  // Ref для отслеживания автозагрузки при монтировании
  const autoLoadTriggeredRef = useRef(false);

  // Простая автозагрузка при монтировании - если есть место для большего количества элементов
  useEffect(() => {
    if (hasMore && onLoadMore && !loading && !autoLoadTriggeredRef.current) {
      // Определяем сколько элементов помещается в одном ряду на текущем экране
      // xs=6 зафиксировано, значит 2 элемента в ряду на мобильных
      const itemsPerRow = isMobile ? 12 / 6 : 12 / (md || 4);

      // Автозагрузка только если элементов достаточно для заполнения экрана
      if (items.length >= itemsPerRow) {
        autoLoadTriggeredRef.current = true;
        onLoadMore();
      }
    }
  }, [onLoadMore, items.length, md, isMobile]); // Добавили зависимости для grid

  // Обработчик скролла с поддержкой LoadMore
  const handleScroll = useCallback(
    (event) => {
      onScroll(event);

      // Проверяем нужно ли загрузить еще при скролле
      if (hasMore && onLoadMore && !loading) {
        const { scrollWidth, scrollLeft, clientWidth } = event.target;
        const totalScrollableWidth = scrollWidth - clientWidth;
        const scrollPercentage = scrollLeft / totalScrollableWidth;

        if (scrollPercentage >= 0.8) {
          onLoadMore();
        }
      }
    },
    [hasMore, onLoadMore, loading, onScroll]
  );

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{ pl: { xs: 2, sm: 0 }, pr: { xs: 2, sm: 0 } }}
      test-id="ArenaPostsCarousel"
    >
      <Root>
        <div
          className={classes.scrollable}
          ref={rootRef}
          onScroll={handleScroll}
        >
          <Grid container spacing={3} className={classes.grid}>
            {items.map((item) => (
              <Grid
                item
                xs={6}
                sm={sm}
                md={md}
                lg={lg}
                xl={xl}
                key={item.id}
                className={classes.item}
              >
                <Item variant={variant} post={item} smallTitle={12 / md > 3} />
              </Grid>
            ))}

            {/* Показываем LoadMore компонент в конце карусели для мобильных устройств */}
            {hasMore && !loading && isMobile && (
              <Grid
                item
                xs={6}
                sm={sm}
                md={md}
                lg={lg}
                xl={xl}
                className={classes.item}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LoadMore key={nextPage} onLoadMore={onLoadMore} />
              </Grid>
            )}
          </Grid>
        </div>

        {prev && (
          <div className={classes.prev}>
            <ButtonBase
              classes={{ root: classes.btnRoot }}
              onClick={onClickPrev}
            >
              <SkuflixChevronRightIcon
                className={classes.icon}
                sx={{ transform: "scaleX(-1)" }}
              />
            </ButtonBase>
          </div>
        )}

        {next && (
          <div className={classes.next}>
            <ButtonBase
              classes={{ root: classes.btnRoot }}
              onClick={onClickNext}
            >
              <SkuflixChevronRightIcon className={classes.icon} />
            </ButtonBase>
          </div>
        )}
      </Root>
    </Box>
  );
}

Carousel.defaultProps = {
  items: [],
};

Carousel.propTypes = {
  variant: PropTypes.oneOf(["TITLE_INSIDE", "TITLE_OUTSIDE"]),
  xs: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  md: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      thumbnails: PropTypes.array,
      openInNewWindow: PropTypes.bool,
    })
  ),
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  loading: PropTypes.bool,
  nextPage: PropTypes.number,
};
